import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import collegeConfig from '../config/collegeConfig';

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function getDeviceInfo() {
  if (Capacitor.isNativePlatform()) {
    return {
      platform: Capacitor.getPlatform(), // 'android' or 'ios'
      deviceType: 'mobile'
    };
  }
  const userAgent = navigator.userAgent;
  let deviceType = 'desktop';
  if (/Mobile|Android|iPhone/i.test(userAgent)) deviceType = 'mobile';
  else if (/iPad|Tablet/i.test(userAgent)) deviceType = 'tablet';
  return { userAgent, platform: navigator.platform, deviceType };
}

// ─── PERMISSION ──────────────────────────────────────────────────────────────

export async function requestNotificationPermission() {
  if (Capacitor.isNativePlatform()) {
    const result = await PushNotifications.requestPermissions();
    return result.receive === 'granted';
  }

  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  return false;
}

// ─── SUBSCRIBE ───────────────────────────────────────────────────────────────

let _nativeRegistrationInProgress = false;

export async function subscribeToPushNotifications() {
  if (Capacitor.isNativePlatform()) {
    if (_nativeRegistrationInProgress) {
      return { success: false, error: 'Registration already in progress' };
    }
    _nativeRegistrationInProgress = true;
    try {
      return await subscribeNative();
    } finally {
      _nativeRegistrationInProgress = false;
    }
  }
  return subscribeWeb();
}

async function subscribeNative() {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) throw new Error('User not authenticated');

    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') throw new Error('Notification permission denied');

    // Remove any existing listeners to avoid duplicates
    await PushNotifications.removeAllListeners();

    await PushNotifications.register();

    return new Promise((resolve) => {
      PushNotifications.addListener('registration', async (registrationToken) => {
        try {
          const response = await fetch(`${collegeConfig.api.baseURL}/push/subscribe-fcm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
              fcmToken: registrationToken.value,
              deviceInfo: getDeviceInfo()
            })
          });

          if (!response.ok) {
            const err = await response.json();
            throw new Error(err.message || 'Failed to save FCM token');
          }

          const result = await response.json();
          localStorage.setItem('fcmToken', registrationToken.value);
          resolve({ success: true, subscription: result });
        } catch (err) {
          resolve({ success: false, error: err.message });
        }
      });

      PushNotifications.addListener('registrationError', (error) => {
        resolve({ success: false, error: error.error });
      });
    });
  } catch (error) {
    console.error('Error subscribing (native):', error);
    return { success: false, error: error.message };
  }
}

async function subscribeWeb() {
  try {
    if (!('serviceWorker' in navigator)) throw new Error('Service workers are not supported');

    const authToken = localStorage.getItem('token');
    if (!authToken) throw new Error('User not authenticated');

    const permissionGranted = await requestNotificationPermission();
    if (!permissionGranted) throw new Error('Notification permission denied');

    const registration = await navigator.serviceWorker.ready;

    const response = await fetch(`${collegeConfig.api.baseURL}/push/vapid-public-key`);
    if (!response.ok) throw new Error('Failed to get VAPID public key');
    const { publicKey } = await response.json();

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
    }

    const subscribeResponse = await fetch(`${collegeConfig.api.baseURL}/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ subscription: subscription.toJSON(), deviceInfo: getDeviceInfo() })
    });

    if (!subscribeResponse.ok) {
      const errorData = await subscribeResponse.json();
      throw new Error(errorData.message || 'Failed to save subscription on server');
    }

    const result = await subscribeResponse.json();
    console.log('✅ Web push subscription successful');
    return { success: true, subscription: result };
  } catch (error) {
    console.error('Error subscribing (web):', error);
    return { success: false, error: error.message };
  }
}

// ─── UNSUBSCRIBE ─────────────────────────────────────────────────────────────

export async function unsubscribeFromPushNotifications() {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) throw new Error('User not authenticated');

    if (Capacitor.isNativePlatform()) {
      // Get the current FCM token to tell server which to deactivate
      // We stored it during registration — re-register to get it back
      await PushNotifications.removeAllListeners();

      return new Promise((resolve) => {
        PushNotifications.addListener('registration', async (registrationToken) => {
          try {
            await fetch(`${collegeConfig.api.baseURL}/push/unsubscribe`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify({ fcmToken: registrationToken.value })
            });
            await PushNotifications.removeAllListeners();
            console.log('✅ Unsubscribed from FCM notifications');
            resolve({ success: true });
          } catch (err) {
            resolve({ success: false, error: err.message });
          }
        });

        PushNotifications.addListener('registrationError', (error) => {
          resolve({ success: false, error: error.error });
        });

        PushNotifications.register();
      });
    }

    // Web unsubscribe
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();

      await fetch(`${collegeConfig.api.baseURL}/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });

      console.log('✅ Unsubscribed from web push notifications');
      return { success: true };
    }

    return { success: false, message: 'No subscription found' };
  } catch (error) {
    console.error('Error unsubscribing:', error);
    return { success: false, error: error.message };
  }
}

// ─── CHECK STATUS ─────────────────────────────────────────────────────────────

export async function checkSubscriptionStatus() {
  try {
    const authToken = localStorage.getItem('token');

    if (Capacitor.isNativePlatform()) {
      if (!authToken) return { subscribed: false, permission: 'default', isNative: true };

      const permResult = await PushNotifications.checkPermissions();
      const permission = permResult.receive; // 'granted', 'denied', 'prompt'

      // If permission not granted, device is definitely not subscribed — don't touch server
      if (permission !== 'granted') {
        return { subscribed: false, permission, isNative: true };
      }

      // Permission granted — check if this device has a registered FCM token locally
      const fcmToken = localStorage.getItem('fcmToken');
      if (!fcmToken) {
        return { subscribed: false, permission, isNative: true };
      }

      // Token exists locally — confirm it's still active on the server
      try {
        const response = await fetch(`${collegeConfig.api.baseURL}/push/subscription-status`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (response.ok) {
          const data = await response.json();
          return {
            subscribed: data.subscribed,
            permission,
            activeDevices: data.activeDevices,
            devices: data.devices,
            isNative: true
          };
        }
      } catch (err) {
        console.error('Error checking server subscription status:', err);
      }

      return { subscribed: false, permission, isNative: true };
    }

    // Web
    if (!('serviceWorker' in navigator)) {
      return { subscribed: false, reason: 'Service workers not supported' };
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (authToken && subscription) {
      try {
        const response = await fetch(`${collegeConfig.api.baseURL}/push/subscription-status`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (response.ok) {
          const data = await response.json();
          return {
            subscribed: data.subscribed,
            permission: Notification.permission,
            activeDevices: data.activeDevices,
            devices: data.devices
          };
        }
      } catch (err) {
        console.error('Error checking server subscription status:', err);
      }
    }

    return {
      subscribed: !!subscription,
      permission: Notification.permission,
      subscription: subscription ? subscription.toJSON() : null
    };
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return { subscribed: false, error: error.message };
  }
}

// ─── SEND TEST ────────────────────────────────────────────────────────────────

export async function sendTestNotification() {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) throw new Error('User not authenticated');

    const response = await fetch(`${collegeConfig.api.baseURL}/push/test`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${authToken}` }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to send test notification');
    }

    const result = await response.json();
    return { success: true, result };
  } catch (error) {
    console.error('Error sending test notification:', error);
    return { success: false, error: error.message };
  }
}
